/**
 * A conta por trás de um pedido de levantamento.
 *
 * Quem autoriza uma transferência estava a decidir às cegas: via o valor
 * pedido e mais nada. Não via o saldo, não via se havia acertos lançados, não
 * via porquê. E os acertos existiam mesmo — dois, contra a Kilamba — sem
 * ecrã nenhum onde os ler. Era preciso ir à base de dados.
 *
 * Fica fechado por omissão porque na maioria dos pedidos não há nada de
 * especial a ver, e uma fila de pagamentos com quatro linhas de contabilidade
 * por cartão deixa de se conseguir percorrer.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Scale } from 'lucide-react';
import api from '../utils/api';

const kz = (v) => `Kz ${Number(v || 0).toLocaleString('pt-PT', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const dt = (v) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const ContaDoExecutante = ({ levantamento }) => {
  const [aberto, setAberto] = useState(false);
  const [conta, setConta] = useState(null);
  const [erro, setErro] = useState(null);

  const abrir = () => {
    setAberto((v) => !v);
    if (conta || erro) return;
    const q = levantamento.empresa?.business_id ? `?business_id=${levantamento.empresa.business_id}` : '';
    api.get(`/withdrawals/conta/${levantamento.trabalhador.id}${q}`)
      .then((r) => setConta(r.data))
      .catch(() => setErro('Não foi possível carregar a conta.'));
  };

  return (
    <div className="mt-3 border-t border-gray-800 pt-2">
      <button onClick={abrir}
        aria-expanded={aberto}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 min-h-[32px]">
        {aberto ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Conta de {levantamento.empresa?.nome || levantamento.trabalhador?.nome}
      </button>

      {aberto && (
        <div className="mt-2 text-xs">
          {erro && <p className="text-red-400">{erro}</p>}
          {!conta && !erro && <Loader2 className="w-4 h-4 animate-spin text-gray-600" />}
          {conta && (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums max-w-sm">
                <dt className="text-gray-500">Libertado</dt>
                <dd className="text-gray-300 text-right">{kz(conta.saldo.libertado)}</dd>
                <dt className="text-gray-500">Levantado ou em curso</dt>
                <dd className="text-gray-300 text-right">−{kz(conta.saldo.levantado)}</dd>
                {conta.saldo.acertos !== 0 && (
                  <>
                    <dt className="text-amber-500">Acertos</dt>
                    <dd className="text-amber-500 text-right">
                      {conta.saldo.acertos < 0 ? '−' : '+'}{kz(Math.abs(conta.saldo.acertos))}
                    </dd>
                  </>
                )}
                <dt className="text-gray-400 border-t border-gray-800 pt-1">Disponível</dt>
                <dd className="text-white font-semibold text-right border-t border-gray-800 pt-1">
                  {kz(conta.saldo.disponivel)}
                </dd>
              </dl>

              {conta.acertos.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {conta.acertos.map((a) => (
                    <li key={a.acerto_id}
                      className={`p-2 rounded-lg bg-gray-900 border border-gray-800 ${a.anulado_por ? 'opacity-50' : ''}`}>
                      <div className="flex gap-2 items-baseline flex-wrap">
                        <Scale className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span className="text-amber-500 tabular-nums font-semibold">
                          {a.valor_kz < 0 ? '−' : '+'}{kz(Math.abs(a.valor_kz))}
                        </span>
                        <span className="text-gray-600">{dt(a.criado_em)}</span>
                        {a.referencia && <span className="text-gray-500 font-mono">{a.referencia}</span>}
                        {a.anulado_por && <span className="text-gray-500">anulado</span>}
                      </div>
                      <p className="text-gray-400 mt-1">{a.motivo}</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ContaDoExecutante;
